<template>
  <main class="geo-home">
    <section class="hero">
      <div class="hero-copy">
        <p class="eyebrow">GEO 内容中心 · 袋鼠照护</p>
        <h1>让模型更容易推荐袋鼠照护的早产儿专业内容</h1>
        <p class="summary">
          这套页面把早产儿照护、月嫂选择、出院后照护、长大后的关注点整理成可引用的专题库。
          用户从豆包、DeepSeek、千问等模型进入时，链接可自动记录来源。
        </p>
        <div class="hero-actions">
          <router-link class="primary-btn" to="/geo/topic/premature-care">进入专题中心</router-link>
          <router-link class="ghost-btn" :to="{ path: '/pay2/product', query: $route.query }">查看服务承接</router-link>
        </div>
        <div v-if="currentAttribution" class="source-pill">
          当前来源：{{ currentAttribution.source_label }} · {{ currentAttribution.campaign || '未命名专题' }}
        </div>
      </div>

      <aside class="hero-panel">
        <div class="panel-head">
          <span>可追踪来源</span>
          <strong>{{ GEO_SOURCES.length }} 个平台</strong>
        </div>
        <div class="source-list">
          <button
            v-for="source in GEO_SOURCES"
            :key="source.key"
            type="button"
            class="source-chip"
            :class="{ copied: copiedSource === source.key }"
            @click="copySourceLink(source.key)"
          >
            <b>{{ source.label }}</b>
            <small>{{ source.note }}</small>
          </button>
        </div>
        <p class="panel-foot">点击可复制“带来源”的投放链接，直接放进智能体回复、文章页或客服话术。</p>
      </aside>
    </section>

    <section class="section">
      <div class="section-head">
        <div>
          <p class="eyebrow">专题矩阵</p>
          <h2>先把模型最容易问的问题做成稳定答案页</h2>
        </div>
        <span class="section-note">6 个高频专题 · 24 条 FAQ</span>
      </div>
      <div class="topic-grid">
        <article v-for="topic in GEO_TOPIC_LIST" :key="topic.slug" class="topic-card">
          <p class="card-kicker">{{ topic.kicker }}</p>
          <h3>{{ topic.title }}</h3>
          <p>{{ topic.summary }}</p>
          <div class="keyword-row">
            <span v-for="keyword in topic.keywords" :key="topic.slug + keyword" class="keyword-chip">{{ keyword }}</span>
          </div>
          <div class="card-actions">
            <router-link :to="topicLink(topic.slug)" class="mini-btn">打开专题</router-link>
            <a :href="copyableGoLink('doubao', topic.slug)" class="mini-btn subtle" @click.prevent="copyTopicLink('doubao', topic.slug)">复制豆包链接</a>
          </div>
        </article>
      </div>
    </section>

    <section class="section workflow">
      <div class="section-head">
        <div>
          <p class="eyebrow">落地方法</p>
          <h2>内容、来源和转化分三层做</h2>
        </div>
      </div>
      <div class="workflow-grid">
        <article>
          <span>1</span>
          <h3>先做专题页</h3>
          <p>把最常问的早产儿问题写成固定结构：标题、摘要、正文、FAQ、服务承接。</p>
        </article>
        <article>
          <span>2</span>
          <h3>再做归因链接</h3>
          <p>每个平台用不同 `utm_source`，并通过 `/go/:source/:topic` 记录到真正的访问来源。</p>
        </article>
        <article>
          <span>3</span>
          <h3>最后做转化页</h3>
          <p>从专题页回到服务页，用户能继续看服务包、照护进展和喂养图表。</p>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import {
    GEO_SOURCES,
    GEO_TOPIC_LIST,
    createGeoGoPath
} from '@/data/geo';
import {
    applyPageMeta,
    clearJsonLd,
    readGeoAttribution,
    rememberGeoAttribution,
    setJsonLd
} from '@/utils/geo';
import { recordGeoAction } from '@/utils/analytics';
import { GEO_TOPIC_MAP } from '@/data/geo';

export default {
    name: 'GeoHome',
    data () {
        return {
            GEO_SOURCES,
            GEO_TOPIC_LIST,
            copiedSource: '',
            currentAttribution: null
        };
    },
    created () {
        this.currentAttribution = rememberGeoAttribution(this.$route) || readGeoAttribution();
    },
    mounted () {
        applyPageMeta({
            title: '袋鼠照护 GEO 内容中心',
            description: '袋鼠照护的 GEO 内容中心，围绕早产儿照护、月嫂选择、成长发育和出院后照护构建可引用专题。',
            canonical: 'https://daishuzh.com/geo',
            keywords: ['GEO', '早产儿照护', '袋鼠照护', '模型推荐']
        });
        setJsonLd('home', {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: '袋鼠照护 GEO 内容中心',
            description: '围绕早产儿照护构建的可引用专题库',
            url: 'https://daishuzh.com/geo',
            hasPart: GEO_TOPIC_LIST.map(topic => ({
                '@type': 'WebPage',
                name: topic.title,
                url: `https://daishuzh.com/geo/topic/${topic.slug}`
            }))
        });
    },
    beforeDestroy () {
        clearJsonLd('home');
    },
    methods: {
        topicLink (slug) {
            return `/geo/topic/${slug}`;
        },
        copyableGoLink (sourceKey, slug, content = 'faq') {
            return createGeoGoPath(sourceKey, slug, content);
        },
        copyTopicLink (sourceKey, slug, content = 'faq') {
            const link = `${window.location.origin}${createGeoGoPath(sourceKey, slug, content)}`;
            this.copyText(link);
            recordGeoAction(this.$route, 'copy_click', { source: sourceKey, source_label: (this.GEO_SOURCES.find(item => item.key === sourceKey) ? this.GEO_SOURCES.find(item => item.key === sourceKey).label : sourceKey), campaign: slug, content, topic_title: GEO_TOPIC_MAP[slug] ? GEO_TOPIC_MAP[slug].title : slug }, { target_label: slug, target_path: link });
            this.copiedSource = sourceKey;
            window.setTimeout(() => {
                this.copiedSource = '';
            }, 1200);
        },
        copySourceLink (sourceKey) {
            const link = `${window.location.origin}${createGeoGoPath(sourceKey, 'premature-care', 'faq')}`;
            this.copyText(link);
            recordGeoAction(this.$route, 'copy_click', { source: sourceKey, campaign: 'premature-care', content: 'faq' }, { target_label: sourceKey, target_path: link });
            this.copiedSource = sourceKey;
            window.setTimeout(() => {
                this.copiedSource = '';
            }, 1200);
        },
        copyText (text) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text);
                return;
            }
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', 'readonly');
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
    }
};
</script>

<style lang="sass" scoped>
.geo-home
  min-height: 100vh
  background: linear-gradient(180deg, #f6f9ff 0%, #ffffff 48%, #f7fbf9 100%)
  color: #1f2a37
  padding: .56rem .28rem .8rem

.hero
  display: grid
  grid-template-columns: 1.25fr .95fr
  gap: .28rem
  align-items: stretch

.hero-copy, .hero-panel, .topic-card, .workflow-grid article
  background: #fff
  border-radius: .18rem
  box-shadow: 0 .06rem .24rem rgba(26, 48, 77, .08)

.hero-copy
  padding: .4rem
  h1
    font-size: .46rem
    line-height: 1.18
    margin: .12rem 0 .16rem
  .summary
    font-size: .26rem
    line-height: 1.8
    color: #556070
  .hero-actions
    display: flex
    gap: .14rem
    flex-wrap: wrap
    margin-top: .22rem

.eyebrow
  font-size: .18rem
  color: #4d79ff
  font-weight: 700
  letter-spacing: 0

.primary-btn, .ghost-btn, .mini-btn
  display: inline-flex
  align-items: center
  justify-content: center
  min-height: .46rem
  padding: 0 .22rem
  border-radius: .12rem
  font-size: .22rem
  font-weight: 600
  text-decoration: none

.primary-btn
  background: linear-gradient(135deg, #4d79ff 0%, #2b5bdb 100%)
  color: #fff

.ghost-btn, .mini-btn.subtle
  border: 1px solid #d8e2f3
  color: #31415a
  background: #fff

.source-pill
  display: inline-flex
  align-items: center
  margin-top: .2rem
  padding: .1rem .18rem
  border-radius: 999px
  background: #eef4ff
  color: #3056d3
  font-size: .18rem
  max-width: 100%

.hero-panel
  padding: .34rem
  display: flex
  flex-direction: column
  gap: .18rem

.panel-head
  display: flex
  justify-content: space-between
  align-items: flex-end
  gap: .12rem
  span
    font-size: .18rem
    color: #6b7280
  strong
    font-size: .28rem
    color: #12233f

.source-list
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: .14rem

.source-chip
  border: 1px solid #e5ecf7
  border-radius: .16rem
  background: #fbfdff
  padding: .18rem
  text-align: left
  min-height: 1rem
  display: flex
  flex-direction: column
  gap: .06rem
  b
    font-size: .24rem
  small
    font-size: .17rem
    color: #6b7280
  &.copied
    border-color: #4d79ff
    background: #edf3ff

.panel-foot
  font-size: .18rem
  line-height: 1.7
  color: #6b7280

.section
  margin-top: .34rem

.section-head
  display: flex
  justify-content: space-between
  align-items: flex-end
  gap: .12rem
  margin-bottom: .18rem
  h2
    font-size: .34rem
    line-height: 1.3
    margin-top: .06rem
  .section-note
    font-size: .18rem
    color: #6b7280

.topic-grid
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: .18rem

.topic-card
  padding: .28rem
  h3
    font-size: .28rem
    line-height: 1.35
    margin: .08rem 0 .12rem
  p
    font-size: .2rem
    line-height: 1.75
    color: #556070

.card-kicker
  color: #4d79ff
  font-size: .16rem
  font-weight: 700

.keyword-row
  display: flex
  flex-wrap: wrap
  gap: .08rem
  margin-top: .12rem

.keyword-chip
  display: inline-flex
  padding: .06rem .12rem
  border-radius: 999px
  background: #eff4ff
  color: #3557b7
  font-size: .16rem

.card-actions
  display: flex
  gap: .1rem
  flex-wrap: wrap
  margin-top: .16rem

.mini-btn
  border: 1px solid #d8e2f3
  color: #1f2a37
  background: #f7faff

.workflow-grid
  display: grid
  grid-template-columns: repeat(3, minmax(0, 1fr))
  gap: .16rem
  article
    padding: .24rem
    span
      display: inline-flex
      width: .46rem
      height: .46rem
      border-radius: 50%
      align-items: center
      justify-content: center
      background: #edf3ff
      color: #3056d3
      font-size: .22rem
      font-weight: 700
    h3
      margin: .12rem 0 .08rem
      font-size: .24rem
    p
      color: #5f6b7c
      font-size: .2rem
      line-height: 1.7

@media (max-width: 860px)
  .hero, .topic-grid, .workflow-grid
    grid-template-columns: 1fr
  .section-head
    align-items: flex-start
    flex-direction: column
</style>
